import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useData } from "@/context/DataContext";
import { Panel, PageHeader } from "@/components/Widgets";
import { fmt } from "@/lib/api";

const Th = ({ children, right }) => <th className={`py-2 pr-3 text-[11px] uppercase tracking-wider text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`py-2 pr-3 ${right ? "text-right" : ""} ${mono ? "num" : ""} ${className}`}>{children}</td>;

const Escalation = ({ ents, labels }) => (
  <table className="w-full min-w-[760px] text-sm" data-testid="assumptions-escalation-table">
    <thead><tr><Th>Entity</Th><Th>Section</Th><Th>Line item</Th><Th>Basis</Th><Th right>Escalation</Th><Th>Source</Th><Th right>Y1 value</Th></tr></thead>
    <tbody>
      {ents.flatMap((e) => Object.entries(e.sections).flatMap(([sec, lines]) =>
        lines.filter((l) => l.escalation != null || l.basis_note).map((l) => (
          <tr key={l.key} className="border-t border-slate-800">
            <Td className="text-slate-300">{e.short}</Td><Td className="text-slate-400">{labels[sec]}</Td><Td className="text-slate-100">{l.label}</Td>
            <Td className="text-slate-500">{e.basis?.[sec] || "—"}</Td>
            <Td right mono className="text-amber-400">{l.escalation != null ? `${(l.escalation * 100).toFixed(1)}%` : l.basis_note}</Td>
            <Td className="text-slate-400">{l.source}</Td><Td right mono>{fmt(l.values[0])}</Td>
          </tr>
        ))))}
    </tbody>
  </table>
);

const Switches = ({ ncf }) => (
  <table className="w-full text-sm" data-testid="assumptions-switch-table">
    <thead><tr><Th>NCF line</Th><Th>Group</Th><Th>Role</Th><Th right>Switch</Th><Th right>Escalation</Th></tr></thead>
    <tbody>
      {ncf.lines.map((l) => (
        <tr key={l.row} className="border-t border-slate-800">
          <Td className="text-slate-100">{l.label}</Td><Td className="capitalize text-slate-400">{l.group}</Td>
          <Td className="text-slate-400">{l.role.replace(/_/g, " ")}</Td>
          <Td right mono className={l.switch === 0 ? "text-rose-400" : "text-emerald-400"}>{l.switch ?? "—"}</Td>
          <Td right mono className="text-amber-400">{l.incr != null ? `${(l.incr * 100).toFixed(0)}%` : "—"}</Td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Rates = ({ ents }) => (
  <table className="w-full min-w-[700px] text-sm" data-testid="assumptions-rates-table">
    <thead><tr><Th>Entity</Th><Th>Facility</Th><Th>Type</Th><Th>Lender</Th><Th right>Interest rate</Th><Th right>Drawdown % of capex</Th></tr></thead>
    <tbody>
      {ents.flatMap((e) => e.loans.map((l, i) => (
        <tr key={`${e.code}-${i}`} className="border-t border-slate-800">
          <Td className="text-slate-300">{e.short}</Td><Td className="text-slate-100">{l.name}</Td><Td className="text-slate-400">{l.type || "—"}</Td>
          <Td className="text-slate-400">{l.lender || "—"}</Td>
          <Td right mono className="text-amber-400">{l.rate != null ? `${(l.rate * 100).toFixed(2)}%` : "—"}</Td>
          <Td right mono>{l.drawdown_ratio != null ? `${(l.drawdown_ratio * 100).toFixed(0)}%` : "—"}</Td>
        </tr>
      )))}
    </tbody>
  </table>
);

const Dependencies = ({ deps, stats }) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
    <table className="w-full text-sm" data-testid="assumptions-dependency-table">
      <thead><tr><Th>Source sheet</Th><Th>→ Feeds</Th><Th right>References</Th></tr></thead>
      <tbody>
        {deps.map((d) => (
          <tr key={`${d.from}-${d.to}`} className="border-t border-slate-800">
            <Td className="text-slate-100">{d.from}</Td><Td className="text-amber-400">{d.to}</Td><Td right mono>{d.count}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <table className="w-full text-sm" data-testid="assumptions-sheet-stats-table">
      <thead><tr><Th>Worksheet</Th><Th>State</Th><Th right>Formulas</Th><Th right>Inputs</Th></tr></thead>
      <tbody>
        {stats.map((s) => (
          <tr key={s.sheet} className="border-t border-slate-800">
            <Td className="text-slate-100">{s.sheet}</Td><Td className="text-slate-500">{s.state}</Td><Td right mono>{s.formulas}</Td><Td right mono>{s.inputs}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OtherSheets = ({ sheets }) => (
  <div className="space-y-6">
    {sheets.map((s) => (
      <div key={s.name} data-testid={`other-sheet-${s.name}`}>
        <div className="mb-2 text-sm font-semibold text-slate-100">{s.name}</div>
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="text-xs">
            <tbody>
              {s.grid.map((r, i) => (
                <tr key={i} className="border-t border-slate-800/60">
                  {r.map((c, j) => <td key={j} className={`px-3 py-1.5 ${typeof c === "number" ? "num text-right text-slate-200" : "text-slate-400"}`}>{c ?? ""}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </div>
);

export default function Assumptions() {
  const { data, filters } = useData();
  const m = data.model;
  const fe = m.entities.find((x) => x.code === filters.entity);
  const ents = fe ? [fe] : m.entities;
  const ncf = fe ? { ...m.ncf, lines: m.ncf.lines.filter((l) => l.entity_code === fe.code) } : m.ncf;
  return (
    <div data-testid="assumptions-page">
      <PageHeader kicker="Inputs, switches & model wiring" title={fe ? `Assumptions - ${fe.short}` : "Assumptions"} />
      <Tabs defaultValue="esc">
        <TabsList className="mb-4 flex h-auto flex-wrap bg-slate-900/70">
          <TabsTrigger value="esc" data-testid="assumptions-tab-escalation">Escalation %</TabsTrigger>
          <TabsTrigger value="sw" data-testid="assumptions-tab-switches">Switches</TabsTrigger>
          <TabsTrigger value="rates" data-testid="assumptions-tab-rates">Interest rates</TabsTrigger>
          <TabsTrigger value="deps" data-testid="assumptions-tab-dependencies">Formula dependencies</TabsTrigger>
          <TabsTrigger value="other" data-testid="assumptions-tab-other">Other sheets</TabsTrigger>
        </TabsList>
        <Panel>
          <div className="overflow-x-auto">
            <TabsContent value="esc"><Escalation ents={ents} labels={data.section_labels} /></TabsContent>
            <TabsContent value="sw"><Switches ncf={ncf} /></TabsContent>
            <TabsContent value="rates"><Rates ents={ents} /></TabsContent>
            <TabsContent value="deps"><Dependencies deps={m.dependencies} stats={m.sheet_stats} /></TabsContent>
            <TabsContent value="other"><OtherSheets sheets={m.other_sheets} /></TabsContent>
          </div>
        </Panel>
      </Tabs>
    </div>
  );
}
